import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { X, Calendar, DollarSign, AlertTriangle, Loader2 } from 'lucide-react';
import blackoutDateService, { BlackoutDate } from '../../services/blackoutDateService';

interface BlackoutDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (blackoutDate: BlackoutDate) => void;
  hotelId: number;
  blackoutDate?: BlackoutDate | null;
  mode: 'create' | 'edit';
}

const BlackoutDateModal: React.FC<BlackoutDateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  hotelId,
  blackoutDate,
  mode
}) => {
  const [formData, setFormData] = useState({
    blackout_from: '',
    blackout_to: '',
    reason: '',
    special_rate: '',
    is_hotel_unavailable: false,
    description: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && blackoutDate) {
        setFormData({
          blackout_from: blackoutDate.blackout_from,
          blackout_to: blackoutDate.blackout_to,
          reason: blackoutDate.reason || '',
          special_rate: blackoutDate.special_rate?.toString() || '',
          is_hotel_unavailable: blackoutDate.is_hotel_unavailable || false,
          description: blackoutDate.description || '',
          status: blackoutDate.status || 'active'
        });
      } else {
        setFormData({
          blackout_from: '',
          blackout_to: '',
          reason: '',
          special_rate: '',
          is_hotel_unavailable: false,
          description: '',
          status: 'active'
        });
      }
      setError(null);
    }
  }, [isOpen, mode, blackoutDate]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.blackout_from) {
      setError('Blackout from date is required');
      return false;
    }

    if (!formData.blackout_to) {
      setError('Blackout to date is required');
      return false;
    }

    if (new Date(formData.blackout_from) > new Date(formData.blackout_to)) {
      setError('Blackout from date cannot be after blackout to date');
      return false;
    }

    if (!formData.reason.trim()) {
      setError('Reason is required');
      return false;
    }

    if (!formData.is_hotel_unavailable && formData.special_rate && isNaN(Number(formData.special_rate))) {
      setError('Special rate must be a valid number');
      return false;
    }

    if (!formData.is_hotel_unavailable && formData.special_rate && Number(formData.special_rate) < 0) {
      setError('Special rate cannot be negative');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const blackoutDateData = {
        hotel_id: hotelId,
        blackout_from: formData.blackout_from,
        blackout_to: formData.blackout_to,
        reason: formData.reason.trim(),
        special_rate: formData.is_hotel_unavailable ? null : (formData.special_rate ? Math.round(parseFloat(formData.special_rate) * 100) / 100 : null),
        is_hotel_unavailable: formData.is_hotel_unavailable,
        description: formData.description.trim(),
        status: formData.status,
        currency: 'INR'
      };

      let response;
      if (mode === 'create') {
        response = await blackoutDateService.createBlackoutDate(blackoutDateData);
      } else {
        response = await blackoutDateService.updateBlackoutDate(blackoutDate!.id!, blackoutDateData);
      }

      if (response.success) {
        onSave(response.data);
        onClose();
      } else {
        setError(response.message || 'Failed to save blackout date');
      }
    } catch (err: any) {
      console.error('Error saving blackout date:', err);
      setError(err.response?.data?.message || 'Failed to save blackout date');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-gray-900 border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-white">
            {mode === 'create' ? 'Add Blackout Date' : 'Edit Blackout Date'}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-900/20 border border-red-500/50 rounded-md p-3 flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="blackout_from" className="text-gray-300">
                  Blackout From <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="blackout_from"
                  type="date"
                  value={formData.blackout_from}
                  onChange={(e) => handleInputChange('blackout_from', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="blackout_to" className="text-gray-300">
                  Blackout To <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="blackout_to"
                  type="date"
                  value={formData.blackout_to}
                  onChange={(e) => handleInputChange('blackout_to', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="reason" className="text-gray-300">
                Reason <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Enter reason for blackout date"
                rows={2}
                required
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-gray-300">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Enter additional description"
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_hotel_unavailable"
                  checked={formData.is_hotel_unavailable}
                  onChange={(e) => handleInputChange('is_hotel_unavailable', e.target.checked)}
                  className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                />
                <Label htmlFor="is_hotel_unavailable" className="text-gray-300">
                  Mark hotel completely unavailable
                </Label>
              </div>
              
              {formData.is_hotel_unavailable && (
                <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-md p-3">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-400" />
                    <span className="text-yellow-400 text-sm">
                      Hotel will be completely unavailable on this date. TRP-World API will not return this hotel for any bookings on this date.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {!formData.is_hotel_unavailable && (
              <div>
                <Label htmlFor="special_rate" className="text-gray-300">
                  Special Rate (Optional)
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="special_rate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.special_rate}
                    onChange={(e) => handleInputChange('special_rate', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white pl-10"
                    placeholder="Enter special rate"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Leave empty to use standard rates
                </p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  mode === 'create' ? 'Add Blackout Date' : 'Update Blackout Date'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BlackoutDateModal;
